import { useState, useEffect } from "react";
import { X, Loader2, Star, Folder, Clock, BookmarkPlus, Search } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getSavedPrompts, getPromptCollections, getCollectionPrompts } from "@/services/savedPromptService";
import CollectionSelectorModal from "@/components/prompts/CollectionSelectorModal";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import SearchBar from "@/components/ui/SearchBar";
import { useSearchFilter } from "@/hooks/useSearchFilter";

interface PromptLabSidebarProps {
  onPromptSelect: (promptId: string) => void;
  selectedPrompt: string | null;
  globalSearchQuery?: string;
}

const PromptLabSidebar = ({ onPromptSelect, selectedPrompt, globalSearchQuery = "" }: PromptLabSidebarProps) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"saved" | "collections" | "history">("saved");
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState<any[]>([]);
  const [allPrompts, setAllPrompts] = useState<any[]>([]);
  const [collections, setCollections] = useState<any[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [selectedCollectionName, setSelectedCollectionName] = useState<string>("");
  const [collectionPrompts, setCollectionPrompts] = useState<any[]>([]);
  const [sessionHistory, setSessionHistory] = useState<any[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  
  const { 
    searchQuery, 
    handleSearch, 
    clearSearch,
    setSearchQuery
  } = useSearchFilter({
    initialQuery: globalSearchQuery,
    debounceMs: 300
  });
  
  useEffect(() => {
    if (globalSearchQuery) {
      setSearchQuery(globalSearchQuery);
      loadAllPrompts();
    }
  }, [globalSearchQuery]);
  
  useEffect(() => {
    loadContent();
    loadAllPrompts();
  }, [activeTab]);
  
  useEffect(() => {
    if (searchQuery.trim()) {
      setIsSearching(true);
      setIsSearching(false);
    }
  }, [searchQuery]);
  
  const loadAllPrompts = async () => {
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select(`
          id, 
          title, 
          description, 
          content, 
          category_id,
          user_id,
          likes,
          copies
        `)
        .eq('status', 'approved')
        .order('copies', { ascending: false });
        
      if (error) throw error;
      
      setAllPrompts(data || []);
    } catch (error) {
      console.error("Error loading all prompts:", error);
    }
  };
  
  const loadContent = async () => {
    setIsLoading(true);
    
    if (activeTab === "saved") {
      try {
        // Pass false to make sure we get ALL saved prompts, not just ones in collections
        const prompts = await getSavedPrompts(false);
        setSavedPrompts(prompts);
        console.log("PromptLabSidebar loaded saved prompts:", prompts.length);
      } catch (error) {
        console.error("Error loading saved prompts:", error);
        toast.error("Failed to load saved prompts");
      }
    } 
    else if (activeTab === "collections") {
      try {
        const collectionsData = await getPromptCollections();
        setCollections(collectionsData);
        
        setSelectedCollectionId(null);
        setSelectedCollectionName("");
        setCollectionPrompts([]);
      } catch (error) {
        console.error("Error loading collections:", error);
        toast.error("Failed to load collections");
      }
    }
    else if (activeTab === "history") {
      try {
        const { data, error } = await supabase
          .from('product_chat_sessions')
          .select('id, title, created_at, template_id')
          .order('created_at', { ascending: false })
          .limit(20);
          
        if (error) throw error;
        setSessionHistory(data || []);
      } catch (error) {
        console.error("Error loading history:", error);
        toast.error("Failed to load chat history");
      }
    }
    
    setIsLoading(false);
  };
  
  const handleCollectionClick = async (collectionId: string, collectionName: string) => {
    setIsLoading(true);
    setSelectedCollectionId(collectionId);
    setSelectedCollectionName(collectionName);
    
    try {
      const prompts = await getCollectionPrompts(collectionId);
      setCollectionPrompts(prompts);
    } catch (error) {
      console.error("Error loading collection prompts:", error);
      toast.error("Failed to load collection prompts");
    }
    
    setIsLoading(false);
  };
  
  const handleBackToCollections = () => {
    setSelectedCollectionId(null);
    setSelectedCollectionName("");
    setCollectionPrompts([]);
  };
  
  // Helper function to format category data
  const formatCategoryName = (category: any): string => {
    if (!category) return 'Uncategorized';
    
    if (typeof category === 'string') {
      // Try to parse if it looks like JSON
      if (category.startsWith('{')) {
        try {
          const categoryObj = JSON.parse(category);
          return categoryObj.name || 'Uncategorized';
        } catch (error) {
          return category;
        }
      }
      return category;
    } 
    
    if (typeof category === 'object' && category !== null) {
      return category.name || 'Uncategorized';
    }
    
    return 'Uncategorized';
  };
  
  const filterPrompts = (promptList: any[]) => {
    if (!searchQuery.trim()) return promptList;
    
    const query = searchQuery.toLowerCase();
    
    if (globalSearchQuery && allPrompts.length > 0) {
      return allPrompts
        .filter(prompt => {
          const title = prompt.title || "";
          const description = prompt.description || "";
          const content = prompt.content || "";
          
          const titleMatch = title.toLowerCase().includes(query);
          const descMatch = description.toLowerCase().includes(query);
          const contentMatch = content.toLowerCase().includes(query);
          
          return titleMatch || descMatch || contentMatch;
        })
        .slice(0, 20); // Limit to first 20 results for better performance
    }
    
    return promptList.filter(prompt => {
      const title = prompt.custom_title || (prompt.prompt && prompt.prompt.title) || prompt.title || "";
      const description = (prompt.prompt && prompt.prompt.description) || prompt.description || "";
      const content = (prompt.prompt && prompt.prompt.content) || prompt.content || "";
      
      return (
        title.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query) ||
        content.toLowerCase().includes(query)
      );
    });
  };
  
  const renderPromptItem = (prompt: any, isFromCollection = false, isGlobalSearch = false) => {
    let id, title, description, category;
    
    if (isGlobalSearch) {
      id = prompt.id;
      title = prompt.title;
      description = prompt.description;
      category = "Global Search";
    } else {
      id = prompt.prompt ? prompt.prompt.id : prompt.id;
      title = prompt.custom_title || (prompt.prompt ? prompt.prompt.title : prompt.title);
      description = prompt.prompt ? prompt.prompt.description : prompt.description;
      category = prompt.prompt ? prompt.prompt.category : prompt.category;
    }
    
    // Format the category name
    const categoryName = formatCategoryName(category);
    
    return (
      <div
        key={id}
        className={`p-3 rounded-md cursor-pointer transition-colors ${
          selectedPrompt === id
            ? "bg-primary/10 border border-primary/20"
            : "hover:bg-muted border border-transparent"
        }`}
        onClick={() => onPromptSelect(id)}
      >
        <h3 className="font-medium truncate">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {description}
        </p>
        <div className="flex items-center mt-2">
          <Badge variant="outline" className="text-xs">
            {categoryName}
          </Badge>
          {isFromCollection && (
            <Badge variant="secondary" className="text-xs ml-2">
              {selectedCollectionName}
            </Badge>
          )}
          {isGlobalSearch && prompt.copies > 0 && (
            <Badge variant="secondary" className="text-xs ml-2">
              {prompt.copies} copies
            </Badge>
          )}
        </div>
      </div>
    );
  };
  
  const EmptyState = ({ 
    icon: Icon, 
    title, 
    description, 
    actionLabel,
    actionHref 
  }: { 
    icon: any, 
    title: string, 
    description: string,
    actionLabel?: string,
    actionHref?: string
  }) => (
    <div className="h-full flex flex-col items-center justify-center text-center p-4">
      <Icon className="h-10 w-10 mb-2 text-muted-foreground/40" />
      <h3 className="text-lg font-medium mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4">
        {description}
      </p>
      {actionLabel && actionHref && (
        <Button asChild size="sm">
          <a href={actionHref}>{actionLabel}</a>
        </Button>
      )}
    </div>
  );
  
  const renderGlobalSearchResults = () => {
    const filteredResults = filterPrompts([]);
    
    if (filteredResults.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p>No prompts match your search.</p>
          <Button variant="link" onClick={clearSearch} className="mt-2">
            Clear search
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-2 px-1">
        <h3 className="text-sm font-medium mb-2 text-muted-foreground">
          Showing {filteredResults.length} results for "{searchQuery}"
        </h3>
        {filteredResults.map((prompt) => renderPromptItem(prompt, false, true))}
      </div>
    );
  };
  
  return (
    <div className="h-full bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 overflow-hidden p-4 flex flex-col">
      <div className="mb-4">
        <SearchBar
          placeholder="Search prompts..."
          onSearch={handleSearch}
          initialValue={searchQuery}
          className="w-full"
          isSearching={isSearching}
          autoFocus={!!globalSearchQuery}
        />
      </div>
      
      {searchQuery.trim() && globalSearchQuery ? (
        <ScrollArea className="flex-1 max-h-[calc(100vh-220px)]">
          {renderGlobalSearchResults()}
        </ScrollArea>
      ) : (
        <>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
            <TabsList className="grid grid-cols-3 w-full mb-4">
              <TabsTrigger value="saved" className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Saved</span>
              </TabsTrigger>
              <TabsTrigger value="collections" className="flex items-center gap-1">
                <Folder className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Collections</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">History</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <ScrollArea className="flex-1">
              {activeTab === "saved" && (
                <>
                  {savedPrompts.length > 0 ? (
                    <div className="space-y-2">
                      {filterPrompts(savedPrompts).map((prompt) => renderPromptItem(prompt))}
                      
                      {filterPrompts(savedPrompts).length === 0 && searchQuery && (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>No saved prompts match your search.</p>
                          <Button variant="link" onClick={clearSearch} className="mt-2">
                            Clear search
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <EmptyState
                      icon={Star}
                      title="No saved prompts found"
                      description="Save prompts to your favorites for easy access"
                      actionLabel="Browse prompts"
                      actionHref="/explore"
                    />
                  )}
                </>
              )}
              
              {activeTab === "collections" && (
                <>
                  {selectedCollectionId ? (
                    <>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleBackToCollections}
                        className="mb-3 flex items-center gap-2"
                      >
                        <X className="h-3.5 w-3.5" />
                        <span>Back to Collections</span>
                      </Button>
                      
                      <h3 className="font-medium mb-3">
                        {selectedCollectionName}
                        <Badge variant="outline" className="ml-2">
                          {collectionPrompts.length} prompt{collectionPrompts.length !== 1 ? 's' : ''}
                        </Badge>
                      </h3>
                      
                      {collectionPrompts.length > 0 ? (
                        <div className="space-y-2">
                          {filterPrompts(collectionPrompts).map((prompt) => 
                            renderPromptItem(prompt, true)
                          )}
                          
                          {filterPrompts(collectionPrompts).length === 0 && searchQuery && (
                            <div className="text-center py-8 text-muted-foreground">
                              <p>No prompts match your search in this collection.</p>
                              <Button variant="link" onClick={clearSearch} className="mt-2">
                                Clear search
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-muted-foreground">
                          <p>This collection is empty.</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {collections.length > 0 ? (
                        <div className="space-y-2">
                          {collections
                            .filter(c => 
                              searchQuery ? c.name.toLowerCase().includes(searchQuery.toLowerCase()) : true
                            )
                            .map((collection) => (
                              <div
                                key={collection.id}
                                className="p-3 rounded-md cursor-pointer transition-colors hover:bg-muted border border-transparent"
                                onClick={() => handleCollectionClick(collection.id, collection.name)}
                              >
                                <div className="flex items-center gap-2">
                                  <Folder className="h-4 w-4 text-primary/70" />
                                  <h3 className="font-medium truncate">{collection.name}</h3>
                                </div>
                                {collection.description && (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1 pl-6">
                                    {collection.description}
                                  </p>
                                )}
                                <div className="text-xs text-muted-foreground mt-1 pl-6">
                                  {collection.prompts_count} prompt{collection.prompts_count !== 1 ? 's' : ''}
                                </div>
                              </div>
                            ))}
                            
                          {collections.filter(c => 
                            searchQuery ? c.name.toLowerCase().includes(searchQuery.toLowerCase()) : true
                          ).length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                              <p>No collections match your search.</p>
                              <Button variant="link" onClick={clearSearch} className="mt-2">
                                Clear search
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <EmptyState
                          icon={Folder}
                          title="No collections found"
                          description="Create collections to organize your prompts"
                          actionLabel="Manage collections"
                          actionHref="/prompt-library"
                        />
                      )}
                    </>
                  )}
                </>
              )}
              
              {activeTab === "history" && (
                <>
                  {sessionHistory.length > 0 ? (
                    <div className="space-y-2">
                      {sessionHistory
                        .filter(session => 
                          searchQuery ? session.title.toLowerCase().includes(searchQuery.toLowerCase()) : true
                        )
                        .map((session) => (
                          <div
                            key={session.id}
                            className="p-3 rounded-md cursor-pointer transition-colors hover:bg-muted border border-transparent"
                            onClick={() => session.template_id && onPromptSelect(session.template_id)}
                          >
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <h3 className="font-medium truncate">{session.title}</h3>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 pl-6">
                              {new Date(session.created_at).toLocaleDateString()} at {new Date(session.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        ))}
                        
                      {sessionHistory.filter(session => 
                        searchQuery ? session.title.toLowerCase().includes(searchQuery.toLowerCase()) : true
                      ).length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>No history matches your search.</p>
                          <Button variant="link" onClick={clearSearch} className="mt-2">
                            Clear search
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <EmptyState
                      icon={Clock}
                      title="No chat history found"
                      description="Your chat history will appear here"
                    />
                  )}
                </>
              )}
            </ScrollArea>
          )}
        </>
      )}
      
      {/* Only show Save to Collection button when not on history tab */}
      {selectedPrompt && activeTab !== "history" && (
        <>
          <Separator className="my-4" />
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => setShowSaveModal(true)}
          >
            <BookmarkPlus className="h-4 w-4 mr-2" />
            Save to Collection
          </Button>
          
          <CollectionSelectorModal
            open={showSaveModal}
            onOpenChange={setShowSaveModal}
            promptId={selectedPrompt}
            promptTitle="Selected Prompt"
            onSuccess={() => {
              toast.success("Prompt saved to collection");
              if (activeTab === "collections") {
                loadContent();
              }
            }}
          />
        </>
      )}
    </div>
  );
};

export default PromptLabSidebar;
